import { useEffect, useState } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import AccountAvatar from '~/account/account-avatar'
import { supabaseGetter } from '~/api/supabase-getter'

/**
 * Fetches a user by ID and returns { full_name, avatar_url }.
 * Only fetches when the component mounts (inside Collapse unmountOnExit).
 */
const useUserById = (userId) => {
  const [user, setUser] = useState(null)

  useEffect(() => {
    async function fetch() {
      if (!userId) { setUser(null); return }
      const { data } = await supabaseGetter.users.getById(userId)
      setUser(data || null)
    }
    fetch()
  }, [userId])

  return user
}

const UserRow = ({ label, userId }) => {
  const user = useUserById(userId)

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <AccountAvatar url={user?.avatar_url} size={28} />
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1, fontSize: '0.6rem' }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
          {user?.full_name ?? '—'}
        </Typography>
      </Box>
    </Box>
  )
}

export const TaskItemDetail = ({ task }) => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        gap: { xs: 1, lg: 2 },
      }}
    >
      {/* User avatar column */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxWidth: { lg: 500 },
          flexShrink: 0,
        }}
      >
        <UserRow label="Created by" userId={task.created_by} />
        {task.completed_by && (
          <UserRow label="Completed by" userId={task.completed_by} />
        )}
      </Box>

      {/* Notes column */}
      {task.notes && (
        <Box
          sx={{
            flex: { lg: 1 },
            borderLeft: { lg: `1px solid ${theme.palette.divider}` },
            pl: { lg: 2 },
            pt: { xs: 0.5, lg: 0 },
            borderTop: { xs: `1px solid ${theme.palette.divider}`, lg: 'none' },
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {task.notes}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
